import { makeAutoObservable } from "mobx";
import currentPortalStore from "../current-portal-store";
import CustomizationClassicStore from "./customization-classic-store";
import CustomizationEdgeStore from "./customization-edge-store";
import CustomizationEdgeFontsStore from "./customization-edge-fonts-store";

class CustomizationStore
{
    private _isLoading: boolean = false;
    private readonly _classicStore: CustomizationClassicStore = new CustomizationClassicStore();
    private readonly _edgeStore: CustomizationEdgeStore = new CustomizationEdgeStore();
    private readonly _edgeFontsStore: CustomizationEdgeFontsStore = new CustomizationEdgeFontsStore();

    public get isLoading(): boolean { return this._isLoading; }

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get isPortalV2Enabled(): boolean { return !!currentPortalStore.setting?.enablePortalV2; }

    public getSelectedStore(): CustomizationEdgeStore | CustomizationClassicStore {
        return this.isPortalV2Enabled ? this._edgeStore : this._classicStore;
    }

    public getClassicStore(): CustomizationClassicStore {
        return this._classicStore;
    }

    public getEdgeStore(): CustomizationEdgeStore {
        return this._edgeStore;
    }

    public getEdgeFontsStore(): CustomizationEdgeFontsStore {
        return this._edgeFontsStore;
    }

    public async load() {
        this._isLoading = true;

        try {
            const [_, edgePayload] = await Promise.all([
                this.getClassicStore().load(),
                this.getEdgeStore().load(),
            ]);

            this.getEdgeFontsStore().setModel(edgePayload);
        } finally{
            this._isLoading = false;
        }
    }

    public async save() {
        const edgeStore = this.getEdgeStore();
        const edgeFontsStore = this.getEdgeFontsStore();
        const classicCustomizationStore = this.getClassicStore();

        const hadPendingFontFiles = edgeFontsStore.hasPendingFileUploads();
        if (hadPendingFontFiles) {
            const latest = await edgeFontsStore.uploadPendingFontFiles();
            if (latest) {
                edgeStore.mergeServerFontState(latest);
            }
        }

        const hasEdgeChanges =
            edgeStore.hasChanges() ||
            edgeFontsStore.hasScalarOrDeleteChangesOnly();

        const saveTasks: Promise<void>[] = [];
        if (hasEdgeChanges) {
            const overlay = edgeFontsStore.getFontOverlayForPut();
            saveTasks.push(edgeStore.save(overlay));
        }
        if (classicCustomizationStore.hasChanges()) {
            saveTasks.push(classicCustomizationStore.save());
        }
        if (saveTasks.length > 0) {
            await Promise.all(saveTasks);
        }

        if (hasEdgeChanges) {
            const m = edgeStore.getModelForSaveAudit();
            if (m) {
                edgeFontsStore.setModel(m);
            }
        }
    }

    public hasChanges(): boolean {
        const edgeCustomizationStore = this.getEdgeStore();
        const edgeFontsStore = this.getEdgeFontsStore();
        const classicCustomizationStore = this.getClassicStore();

        return (
            edgeCustomizationStore.hasChanges() ||
            edgeFontsStore.hasChanges() ||
            classicCustomizationStore.hasChanges()
        );
    }

    public discardChanges(): void {
        const edgeCustomizationStore = this.getEdgeStore();
        const edgeFontsStore = this.getEdgeFontsStore();
        const classicCustomizationStore = this.getClassicStore();

        edgeCustomizationStore.discardChanges();
        edgeFontsStore.discardChanges();
        classicCustomizationStore.discardChanges();
    }
}

const customizationStore = new CustomizationStore();
export default customizationStore;
